import { ButtonHTMLAttributes, PropsWithChildren, useCallback } from 'react';

import { Loading3QuartersOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { debounce } from 'lodash-es';

import { Colors, PrimaryColors } from '../../colors';
import { Flex } from '../Flex';

export type ButtonType = 'primary' | 'default' | 'weak' | 'transparent';
type ButtonSize = 'small' | 'large' | 'xlarge' | 'default';

export interface ButtonProps extends PropsWithChildren<ButtonHTMLAttributes<HTMLButtonElement>> {
  buttonType?: ButtonType;
  buttonSize?: ButtonSize;
  border?: boolean;
  danger?: boolean;
  color?: string;
  /**
   * @deprecated use disabled instead
   */
  disable?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  loading?: boolean;
  ignoreDebounce?: boolean;
}

const getButtonSize = (size?: ButtonSize, iconExist?: boolean) => {
  switch (size) {
    case 'small':
      return {
        height: 24,
        padding: '4px 6px',
        fontSize: 13,
        width: iconExist ? 24 : '',
      };
    case 'large':
      return {
        height: 36,
        width: iconExist ? 36 : '',
        padding: '7px 12px',
        fontSize: 16,
      };
    case 'xlarge':
      return {
        height: 44,
        width: iconExist ? 44 : '',
        padding: '0px 16px',
        fontSize: 16,
        fontWeight: 500,
      };
    default:
      return {
        fontSize: 14,
        height: 32,
        width: iconExist ? 32 : '',
        padding: '6px 8px',
      };
  }
};

const getButtonTypeStyle = (
  type?: ButtonType,
  danger?: boolean,
  border?: boolean,
  loading?: boolean,
) => {
  switch (type) {
    case 'primary':
      return border
        ? css`
            background-color: ${Colors.white};
            border: 1px solid ${danger ? Colors.red500 : PrimaryColors.primary600};
            color: ${danger ? Colors.red700 : PrimaryColors.primary700};
            & svg,
            & path {
              fill: ${danger ? Colors.red700 : PrimaryColors.primary700};
            }
            &:hover {
              background-color: ${danger ? Colors.redOpacity04 : PrimaryColors.primaryOpacity04};
            }
            &:active {
              background-color: ${danger ? Colors.redOpacity07 : PrimaryColors.primaryOpacity07};
            }
          `
        : css`
            background-color: ${danger ? Colors.red500 : PrimaryColors.primary500};
            border: none;
            color: ${Colors.white};
            & svg,
            & path {
              fill: ${loading ? Colors.grayOpacity22 : Colors.white};
            }
            &:hover {
              background-color: ${danger ? Colors.red600 : PrimaryColors.primary600};
              color: ${Colors.white};
              & svg,
              & path {
                fill: ${loading ? Colors.grayOpacity22 : Colors.white};
              }
            }
            &:focus {
              box-shadow: 0px 0px 0px 4px ${PrimaryColors.primaryOpacity36};
            }
            &:active {
              background-color: ${danger ? Colors.red700 : PrimaryColors.primary700};
              color: ${Colors.white};
              & svg,
              & path {
                fill: ${loading ? Colors.grayOpacity22 : Colors.white};
              }
            }
          `;
    case 'weak':
      return border
        ? css`
            background-color: ${Colors.white};
            color: ${danger ? Colors.red700 : Colors.gray700};
            & svg,
            & path {
              fill: ${danger ? Colors.red700 : Colors.gray700};
            }
            &:hover {
              background-color: ${Colors.grayOpacity04};
            }
            &:active {
              background-color: ${Colors.grayOpacity07};
            }
          `
        : css`
            background-color: ${danger ? Colors.redOpacity12 : PrimaryColors.primaryOpacity12};
            color: ${danger ? Colors.red700 : PrimaryColors.primary900};
            & svg,
            & path {
              fill: ${danger ? Colors.red700 : PrimaryColors.primary900};
            }
            &:hover {
              background-color: ${danger ? Colors.redOpacity22 : PrimaryColors.primaryOpacity22};
            }
            &:active {
              background-color: ${danger ? Colors.redOpacity36 : PrimaryColors.primaryOpacity36};
            }
          `;
    case 'transparent':
      return css`
        background-color: transparent;
        color: ${danger ? Colors.red700 : Colors.gray700};
        & svg,
        & path {
          fill: ${danger ? Colors.red700 : Colors.gray700};
        }
        &:hover {
          background-color: ${Colors.grayOpacity04};
        }
        &:active {
          background-color: ${Colors.grayOpacity07};
        }
      `;
    default:
      return border
        ? css`
            background-color: ${Colors.white};
            border: 1px solid ${Colors.grayOpacity12};
            color: ${Colors.gray700};

            &:hover {
              background-color: ${Colors.grayOpacity04};
            }
            &:active {
              background-color: ${Colors.grayOpacity07};
            }
          `
        : css`
            background-color: ${Colors.grayOpacity04};
            color: ${danger ? Colors.red700 : Colors.gray700};

            &:hover {
              background-color: ${Colors.grayOpacity07};
            }
            &:active {
              background-color: ${Colors.grayOpacity12};
            }
          `;
  }
};

export const getDisabledStyle = (type?: ButtonType, danger?: boolean, border?: boolean) => {
  const defaultStyle = css`
    cursor: not-allowed !important;
    pointer-events: none !important;
  `;

  switch (type) {
    case 'primary':
      return border
        ? css`
            ${defaultStyle}
            background-color: ${Colors.white};
            border: 1px solid ${Colors.grayOpacity07};
            color: ${Colors.grayOpacity22};
            & svg,
            & path {
              fill: ${Colors.grayOpacity22};
            }
            & span {
              color: ${Colors.grayOpacity22};
            }
          `
        : danger
          ? css`
              ${defaultStyle}
              background-color: ${Colors.red200};
            `
          : css`
              ${defaultStyle}
              color: ${Colors.grayOpacity12};
              background-color: ${PrimaryColors.primaryOpacity12};
            `;
    case 'weak':
      return border
        ? css`
            ${defaultStyle}
            background-color: ${Colors.white};
            color: ${Colors.grayOpacity22};
            & svg,
            & path {
              fill: ${Colors.grayOpacity22};
            }
          `
        : css`
            ${defaultStyle}
            background-color: ${danger ? Colors.redOpacity12 : PrimaryColors.primaryOpacity12};
            color: ${Colors.grayOpacity22};
            & svg,
            & path {
              fill: ${Colors.grayOpacity22};
            }
          `;
    case 'transparent':
      return border
        ? css`
            ${defaultStyle}
            background-color: ${Colors.white};
            border: 1px solid ${Colors.grayOpacity22};
            color: ${Colors.grayOpacity22};
            & svg,
            & path {
              fill: ${Colors.grayOpacity22};
            }
          `
        : css`
            ${defaultStyle}
            color: ${Colors.grayOpacity22};
            & svg,
            & path {
              fill: ${Colors.grayOpacity22};
            }
          `;
    case 'default':
      return border
        ? css`
            ${defaultStyle}
            background-color: ${Colors.white};
            border: 1px solid ${Colors.grayOpacity07};
            color: ${Colors.grayOpacity22};
            & svg,
            & path {
              fill: ${Colors.grayOpacity22};
            }
            & span {
              color: ${Colors.grayOpacity22};
            }
          `
        : css`
            ${defaultStyle}
            background-color: ${Colors.grayOpacity04};
            color: ${Colors.grayOpacity22};
            & svg,
            & path {
              fill: ${Colors.grayOpacity22};
            }
          `;
  }
};

const getButtonBorderRadius = (buttonSize?: ButtonSize) => {
  switch (buttonSize) {
    case 'small':
      return css`
        border-radius: 7px;
      `;
    case 'large':
      return css`
        border-radius: 10px;
      `;
    case 'xlarge':
      return css`
        border-radius: 12px;
      `;
    default:
      return css`
        border-radius: 8px;
      `;
  }
};

const WrappedButton = styled('button')<
  ButtonProps & {
    iconOnly?: boolean;
  }
>`
  ${({ buttonSize, iconOnly }) => getButtonSize(buttonSize, iconOnly)};
  ${({ buttonType, danger, border, loading }) =>
    getButtonTypeStyle(buttonType, danger, border, loading)};
  ${({ disabled, buttonType, danger, border }) =>
    disabled && getDisabledStyle(buttonType, danger, border)};
  ${({ buttonSize }) => getButtonBorderRadius(buttonSize)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 400;
  &:focus-visible {
    outline: none;
  }
`;

export const Button: React.FC<ButtonProps> = ({
  buttonType = 'default',
  buttonSize = 'default',
  border,
  danger,
  color,
  // @deprecated
  disable,
  disabled,
  children,
  icon,
  style,
  type = 'button',
  onClick,
  loading,
  ignoreDebounce = false,
  ...props
}) => {
  const isDisabled = disable || disabled || loading;

  const debouncedOnClick = useCallback(
    debounce((e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
    }, 300),
    [onClick],
  );

  return (
    <WrappedButton
      buttonType={buttonType}
      buttonSize={buttonSize}
      danger={danger}
      border={border}
      disabled={isDisabled}
      loading={loading}
      iconOnly={Boolean(icon) && !children}
      type={type}
      onClick={ignoreDebounce ? onClick : debouncedOnClick}
      style={{
        cursor: loading ? 'wait' : 'pointer',
        pointerEvents: loading ? 'none' : 'auto',
        color,
        ...style,
      }}
      {...props}
    >
      {icon ? (
        <Flex
          gap={6}
          justify="center"
          align="center"
          style={{
            width: 'max-content',
          }}
        >
          {loading ? <Loading3QuartersOutlined spin /> : icon}
          {children}
        </Flex>
      ) : (
        <Flex
          justify="center"
          style={{
            display: 'inline-flex',
            width: '100%',
          }}
          align="center"
        >
          {loading ? <Loading3QuartersOutlined spin style={{ marginRight: 4 }} /> : <></>}
          {children}
        </Flex>
      )}
    </WrappedButton>
  );
};
